import React from 'react';
import { useForm } from 'react-hook-form';
import type { OperationStep } from '@/types/operations';
import { getApiErrorMessage } from './helpers';
import Modal from '@/components/utils/Modal';
import modalStyles from '@/components/ui/Modal.module.css';
import { Button } from '@/components/ui';
import styles from './OperationsChecklist.module.css';

interface ExemptFormValues {
  reason: string;
}

interface ExemptDialogProps {
  open: boolean;
  step: OperationStep | null;
  // Resolves on success; REJECTS on failure so this dialog can surface the
  // backend `reason` field error inline.
  onSubmit: (reason: string) => Promise<void>;
  onClose: () => void;
}

const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 2000;

const ExemptDialog: React.FC<ExemptDialogProps> = ({ open, step, onSubmit, onClose }) => {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ExemptFormValues>({ defaultValues: { reason: '' } });

  React.useEffect(() => {
    if (open) reset({ reason: '' });
  }, [open, step?.id, reset]);

  const submit = handleSubmit(async ({ reason }) => {
    try {
      await onSubmit(reason.trim());
    } catch (error) {
      setError('reason', {
        type: 'server',
        message: getApiErrorMessage(error, 'Failed to exempt step.'),
      });
    }
  });

  return (
    <Modal
      open={open}
      title="Exempt step"
      onClose={onClose}
      isSubmitting={isSubmitting}
      titleId="exempt-dialog-title"
      footer={
        <>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="exempt-form"
            variant="warning"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Exempting…' : 'Exempt'}
          </Button>
        </>
      }
    >
      <p className={modalStyles.desc}>
        {step
          ? `Waive "${step.title}" with a written reason. This marks the step resolved.`
          : 'Waive this step with a written reason.'}
      </p>

      <form id="exempt-form" onSubmit={submit} noValidate>
        <div className={styles['form-group']}>
          <label htmlFor="exempt-reason">Reason</label>
          <textarea
            id="exempt-reason"
            {...register('reason', {
              required: 'A reason is required.',
              validate: (value) =>
                value.trim().length >= MIN_REASON_LENGTH ||
                `Reason must be at least ${MIN_REASON_LENGTH} characters.`,
              maxLength: {
                value: MAX_REASON_LENGTH,
                message: `Reason must be at most ${MAX_REASON_LENGTH} characters.`,
              },
            })}
            placeholder="Why is this step being waived?"
            autoFocus
            disabled={isSubmitting}
          />
          {errors.reason && (
            <p className={styles['form-error']}>{errors.reason.message}</p>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default ExemptDialog;
